import type {
  DashboardStatsQuery,
  ListActivitiesQuery,
  ListCallsQuery,
  ListContactsQuery,
  ListFollowUpsQuery,
  ListLeadsQuery,
  ListUsersQuery,
} from '@crm/contracts';

/**
 * A key factory, not string literals scattered through components.
 *
 * Keys are hierarchical so invalidation can be as broad or narrow as the change
 * warrants: creating a contact invalidates `contacts.lists()` and leaves every
 * cached detail page untouched, where invalidating `contacts.all` would throw
 * away work the user can still see.
 */
export const queryKeys = {
  me: ['me'] as const,

  contacts: {
    all: ['contacts'] as const,
    lists: () => [...queryKeys.contacts.all, 'list'] as const,
    list: (params: ListContactsQuery) => [...queryKeys.contacts.lists(), params] as const,
    details: () => [...queryKeys.contacts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contacts.details(), id] as const,
    /** The computed pre-call briefing. Its own key so it can refetch alone. */
    briefing: (id: string) => [...queryKeys.contacts.all, 'briefing', id] as const,
    callInsights: (id: string) => [...queryKeys.contacts.all, 'call-insights', id] as const,
    /** §11 — the WhatsApp conversation analysis. */
    chatAnalysis: (id: string) => [...queryKeys.contacts.all, 'chat-analysis', id] as const,
  },
  followUps: {
    all: ['follow-ups'] as const,
    lists: () => [...queryKeys.followUps.all, 'list'] as const,
    list: (params: ListFollowUpsQuery) => [...queryKeys.followUps.lists(), params] as const,
    counts: (mineOnly?: boolean) => [...queryKeys.followUps.all, 'counts', { mineOnly: !!mineOnly }] as const,
  },

  calls: {
    all: ['calls'] as const,
    lists: () => [...queryKeys.calls.all, 'list'] as const,
    list: (params: ListCallsQuery) => [...queryKeys.calls.lists(), params] as const,
    /** One call's §8 analysis, keyed by call so a refetch is per-panel. */
    analysis: (id: number) => [...queryKeys.calls.all, 'analysis', id] as const,
  },
  gemini: {
    settings: ['gemini', 'settings'] as const,
  },
  leads: {
    all: ['leads'] as const,
    lists: () => [...queryKeys.leads.all, 'list'] as const,
    list: (params: ListLeadsQuery) => [...queryKeys.leads.lists(), params] as const,
    details: () => [...queryKeys.leads.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.leads.details(), id] as const,
  },

  /**
   * §14 operators. Its own root, separate from `lookups.users`: that one is a
   * long-lived option list, while this changes the moment an admin moves
   * somebody under a new manager — and that change alters what every other
   * cached list is allowed to show.
   */
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (params: ListUsersQuery) => [...queryKeys.users.lists(), params] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
  },

  /** Reference data. Long-lived, so it gets its own root to keep it out of
   *  the broad invalidations that follow a write. */
  lookups: {
    all: ['lookups'] as const,
    users: () => [...queryKeys.lookups.all, 'users'] as const,
    companies: (search?: string) => [...queryKeys.lookups.all, 'companies', search ?? ''] as const,
    campaigns: () => [...queryKeys.lookups.all, 'campaigns'] as const,
    tags: () => [...queryKeys.lookups.all, 'tags'] as const,
  },

  activities: {
    all: ['activities'] as const,
    list: (params: ListActivitiesQuery) => [...queryKeys.activities.all, 'list', params] as const,
  },

  notes: {
    all: ['notes'] as const,
    forContact: (contactId: string) => [...queryKeys.notes.all, contactId] as const,
  },

  /** §13. `myDay` is under the same root so completing a follow-up can
   *  invalidate the whole dashboard without naming each panel. */
  /** The welcome summary. Its own root: nothing else invalidates it. */
  welcome: { all: ['welcome'] as const },

  dashboard: {
    all: ['dashboard'] as const,
    myDay: (everyone?: boolean) => [...queryKeys.dashboard.all, 'my-day', { everyone: !!everyone }] as const,
    newSellers: (includeWorked?: boolean) =>
      [...queryKeys.dashboard.all, 'new-sellers', { includeWorked: !!includeWorked }] as const,
    stats: (params: DashboardStatsQuery) => [...queryKeys.dashboard.all, 'stats', params] as const,
  },
} as const;
